import * as React from 'react';

import {Label} from '../form/label';
import {TextField} from '../textfield/textfield';
import {ESC} from '../utils/keys';

import {InlineFormWrapper} from './inline-form-wrapper';

type Props = {
    name: string;
    value: string | null | undefined;
    onChange: (newVal: string) => void;
    onSubmit: () => any;
    onCancel: () => any;
    blinkSuccess: boolean;
    shouldAutoFocus?: boolean;
    multiline?: boolean;
    placeholder?: string;
    label?: string;
    type?: string;
};

type State = {
    isFocused: boolean;
    shouldDisableSubmit: boolean;
};

/*
 * Base textfield class responsible for rendering a react
 * "Edit In Place" textfield component, that supports…
 *
 * - Enter-to-submit
 * - Blur-to-submit
 * - Submit/Cancel buttons when focused
 * - Esc-to-cancel
 * - Flashing green text when recently submitted
 */
export class EditInPlaceTextfield extends React.PureComponent<Props, State> {
    textField: HTMLInputElement | null | undefined;

    static defaultProps = {
        type: 'text',
    };

    constructor(props: Props) {
        super(props);
        this.state = {
            isFocused: false,
            shouldDisableSubmit: false,
        };
    }

    componentDidMount() {
        if (this.textField && this.props.shouldAutoFocus) {
            // Not using the autoFocus prop on the TextField below
            // because it happens too fast for the select() callback
            // to happen
            this.textField.focus();
        }
    }

    UNSAFE_componentWillReceiveProps(prevProps: Props, nextProps: Props) {
        // When our value changes, check to see if we can
        // still submit it (only applies to type='email')
        if (prevProps.value !== nextProps.value) {
            if (this.textField && this.props.type === 'email') {
                // If no value at all, disable submitting
                if (!this.props.value || !this.props.value.length) {
                    this.setState({shouldDisableSubmit: true});
                } else {
                    this.setState({shouldDisableSubmit: !this.textField.validity.valid});
                }
            }
        }
    }

    render() {
        return (
            <InlineFormWrapper
                shouldShowControls={this.state.isFocused}
                onSubmit={this.props.onSubmit}
                canSubmit={!this.state.shouldDisableSubmit}
                onCancel={this.handleCancel}
            >
                {this.props.label ? (
                    <Label htmlFor={this.props.name}>{this.props.label}</Label>
                ) : undefined}
                <TextField
                    textFieldRef={(n) => {
                        this.textField = n;
                    }}
                    type={this.props.type}
                    // @ts-expect-error
                    name={this.props.name}
                    onFocus={this.handleFocus}
                    hasError={this.state.shouldDisableSubmit}
                    onBlur={this.handleBlur}
                    onEnter={!this.props.multiline ? this.handleEnter : undefined}
                    onKeyDown={this.handleKeyDown}
                    selectOnFocus={true}
                    blinkSuccess={this.props.blinkSuccess}
                    onChange={this.handleChange}
                    placeholder={this.props.placeholder || `Add ${this.props.name}…`}
                    multiline={this.props.multiline}
                    value={this.props.value || ''}
                />
            </InlineFormWrapper>
        );
    }

    handleChange = (newVal: string) => {
        this.props.onChange(newVal);
    };

    handleEnter = () => {
        if (!this.state.shouldDisableSubmit) {
            this.programaticallyBlurInput();
            this.props.onSubmit();
        }
    };

    handleCancel = () => {
        this.programaticallyBlurInput();
        this.props.onCancel();
    };

    handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.keyCode === ESC) {
            this.handleCancel();
        }
    };

    handleFocus = () => {
        this.setState({isFocused: true});
    };

    handleBlur = () => {
        // We don't want a blur event immediately submitting the input,
        // because that would negate our cancel button. Therefore,
        // we'll use a timeout here and manage blur state separately.
        setTimeout(() => {
            if (this.state.isFocused && this.textField) {
                this.setState({isFocused: false});
                this.props.onSubmit();
            }
        }, 100);
    };

    programaticallyBlurInput = () => {
        if (this.textField) {
            this.setState({isFocused: false}, () => {
                // Flow needs us to check this again, sadly.
                if (this.textField) {
                    this.textField.blur();
                }
            });
        }
    };
}
